import React, { useMemo, useRef, useState } from "react";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import {
  AlarmClock,
  CheckCircle2,
  CircleDashed,
  Download,
  FileText,
  FolderOpen,
  Pencil,
  Plus,
  Search,
  ShieldAlert,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import "./DocumentiAziendali.css";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { database, filesCloud, timestamp } from "../../firebase/config";
import { downloadDichiarazionePdf } from "../../stampe/generatori/dichiarazionePdf";

const COLLECTION = "documentiAziendali";
const DICHIARAZIONI_COLLECTION = "dichiarazioni";
const TIPO_LEGALE_RAPPRESENTANTE = "documento-legale-rappresentante";
const SOGLIA_SCADENZA_GIORNI = 30;
const SOGLIA_PROSSIME_SCADENZE_GIORNI = 90;

const todayInputValue = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

// Elenco fisso dei documenti aziendali gestiti dalla pagina.
// "tipo" viene usato come id del documento su Firestore.
const DOCUMENTI = [
  {
    tipo: "visura",
    nome: "Visura",
    descrizione: "Visura camerale ordinaria",
  },
  {
    tipo: "certificato-cciaa",
    nome: "Certificato iscrizione CCIAA",
    descrizione: "Certificato di iscrizione al Registro delle Imprese",
  },
  {
    tipo: "durc",
    nome: "DURC",
    descrizione: "Documento Unico di Regolarità Contributiva",
  },
  {
    tipo: "durf",
    nome: "DURF",
    descrizione: "Documento Unico di Regolarità Fiscale",
  },
  {
    tipo: "attestato-soa",
    nome: "Attestato SOA",
    descrizione: "Attestazione di qualificazione per lavori pubblici",
  },
  {
    tipo: "attestato-iso",
    nome: "Attestato ISO",
    descrizione: "Certificazione del sistema di gestione qualità",
  },
  {
    tipo: "polizza-rct",
    nome: "Polizza RCT",
    descrizione: "Polizza di Responsabilità Civile verso Terzi",
  },
  {
    tipo: "documento-legale-rappresentante",
    nome: "Documento Legale Rappresentante",
    descrizione: "Documento d'identità del legale rappresentante",
  },
];

const parseData = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const date = parseISO(raw);
  return isNaN(date.getTime()) ? null : date;
};

const formatData = (value) => {
  const date = parseData(value);
  return date ? format(date, "dd/MM/yyyy", { locale: it }) : "—";
};

const getStatoDocumento = (doc) => {
  if (!doc?.fileUrl) {
    return { chiave: "mancante", label: "Da caricare" };
  }
  const scadenza = parseData(doc.dataScadenza);
  if (!scadenza) {
    return { chiave: "disponibile", label: "Disponibile" };
  }
  const giorni = differenceInCalendarDays(scadenza, new Date());
  if (giorni < 0) {
    return { chiave: "scaduto", label: "Scaduto" };
  }
  if (giorni <= SOGLIA_SCADENZA_GIORNI) {
    return { chiave: "in-scadenza", label: "In scadenza" };
  }
  return { chiave: "disponibile", label: "Valido" };
};

const uploadConProgresso = (file, path, onProgress) =>
  new Promise((resolve, reject) => {
    const task = filesCloud.ref(path).put(file);
    task.on(
      "state_changed",
      (snapshot) => {
        if (snapshot.totalBytes) {
          onProgress(
            Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
          );
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await task.snapshot.ref.getDownloadURL();
          resolve({ url, path });
        } catch (err) {
          reject(err);
        }
      },
    );
  });

export default function DocumentiAziendali() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);
  const { documents, error } = useCollection(COLLECTION);
  const { documents: dichiarazioni } = useCollection(
    DICHIARAZIONI_COLLECTION,
    null,
    ["createdAt", "desc"],
  );
  const {
    addDocument: addDichiarazione,
    updateDocument: updateDichiarazione,
    deleteDocument: deleteDichiarazione,
  } = useFirestore(DICHIARAZIONI_COLLECTION);

  const [view, setView] = useState("documenti");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingTipo, setEditingTipo] = useState(null);
  const [formFile, setFormFile] = useState(null);
  const [formDataEmissione, setFormDataEmissione] = useState("");
  const [formDataScadenza, setFormDataScadenza] = useState("");
  const [formNote, setFormNote] = useState("");
  const [savingTipo, setSavingTipo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [feedback, setFeedback] = useState(null);

  const [dichNomeFile, setDichNomeFile] = useState("");
  const [dichOggetto, setDichOggetto] = useState("");
  const [dichCorpo, setDichCorpo] = useState("");
  const [dichLuogo, setDichLuogo] = useState("");
  const [dichData, setDichData] = useState(todayInputValue());
  const [dichEditingId, setDichEditingId] = useState(null);
  const [dichFeedback, setDichFeedback] = useState(null);
  const [dichGenerating, setDichGenerating] = useState(false);
  const dichCorpoRef = useRef(null);

  const documentiPerTipo = useMemo(() => {
    const map = new Map();
    (documents || []).forEach((doc) => {
      map.set(doc.tipo, doc);
    });
    return map;
  }, [documents]);

  const righe = useMemo(
    () =>
      DOCUMENTI.map((def) => ({
        ...def,
        ...(documentiPerTipo.get(def.tipo) || {}),
        tipo: def.tipo,
        nome: def.nome,
        descrizione: def.descrizione,
      })),
    [documentiPerTipo],
  );

  const statistiche = useMemo(() => {
    let disponibili = 0;
    let mancanti = 0;
    let inScadenza = 0;
    let scaduti = 0;
    righe.forEach((row) => {
      const stato = getStatoDocumento(row);
      if (!row.fileUrl) mancanti += 1;
      else disponibili += 1;
      if (stato.chiave === "in-scadenza") inScadenza += 1;
      if (stato.chiave === "scaduto") scaduti += 1;
    });
    return { totale: righe.length, disponibili, mancanti, inScadenza, scaduti };
  }, [righe]);

  const prossimeScadenze = useMemo(
    () =>
      righe
        .filter((row) => parseData(row.dataScadenza))
        .map((row) => ({
          ...row,
          giorni: differenceInCalendarDays(
            parseData(row.dataScadenza),
            new Date(),
          ),
        }))
        .filter((row) => row.giorni <= SOGLIA_PROSSIME_SCADENZE_GIORNI)
        .sort((a, b) => a.giorni - b.giorni),
    [righe],
  );

  const righeFiltrate = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return righe;
    return righe.filter(
      (row) =>
        row.nome.toLowerCase().includes(term) ||
        row.descrizione.toLowerCase().includes(term) ||
        (row.fileName || "").toLowerCase().includes(term),
    );
  }, [righe, searchTerm]);

  const resetForm = () => {
    setEditingTipo(null);
    setFormFile(null);
    setFormDataEmissione("");
    setFormDataScadenza("");
    setFormNote("");
    setUploadProgress(0);
  };

  const startEdit = (row) => {
    setEditingTipo(row.tipo);
    setFormFile(null);
    setFormDataEmissione(row.dataEmissione || "");
    setFormDataScadenza(row.dataScadenza || "");
    setFormNote(row.note || "");
    setUploadProgress(0);
    setFeedback(null);
  };

  const handleSave = async (row) => {
    if (!formFile && !row.fileUrl && !formDataEmissione && !formDataScadenza) {
      setFeedback({
        type: "error",
        text: "Seleziona un file oppure indica almeno una data per registrare il documento.",
      });
      return;
    }

    setSavingTipo(row.tipo);
    setFeedback(null);
    try {
      const payload = {
        tipo: row.tipo,
        nome: row.nome,
        dataEmissione: formDataEmissione || null,
        dataScadenza: formDataScadenza || null,
        note: formNote || "",
        updatedAt: timestamp.fromDate(new Date()),
        updatedBy: user?.uid || null,
      };

      if (formFile) {
        const path = `documentiAziendali/${row.tipo}/${Date.now()}_${formFile.name}`;
        setUploadProgress(1);
        const { url } = await uploadConProgresso(
          formFile,
          path,
          setUploadProgress,
        );
        payload.fileUrl = url;
        payload.fileName = formFile.name;
        payload.filePath = path;
        payload.mimeType = formFile.type || "";
        payload.fileSize = formFile.size || 0;
      }

      await database.collection(COLLECTION).doc(row.tipo).set(payload, {
        merge: true,
      });

      setFeedback({
        type: "success",
        text: `"${row.nome}" aggiornato correttamente.`,
      });
      resetForm();
    } catch (err) {
      console.error("Errore durante il salvataggio del documento:", err);
      setFeedback({
        type: "error",
        text: `Salvataggio di "${row.nome}" non riuscito. Riprova.`,
      });
    } finally {
      setSavingTipo(null);
      setUploadProgress(0);
    }
  };

  const resetDichForm = () => {
    setDichNomeFile("");
    setDichOggetto("");
    setDichCorpo("");
    setDichLuogo("");
    setDichData(todayInputValue());
    setDichEditingId(null);
    setDichFeedback(null);
  };

  // Avvolge il testo selezionato nel markup inline (grassetto/corsivo).
  const applyMarkup = (marker) => {
    const el = dichCorpoRef.current;
    const start = el?.selectionStart ?? dichCorpo.length;
    const end = el?.selectionEnd ?? dichCorpo.length;
    const selected = dichCorpo.slice(start, end) || "testo";
    const next = `${dichCorpo.slice(0, start)}${marker}${selected}${marker}${dichCorpo.slice(end)}`;
    setDichCorpo(next);

    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(
        start + marker.length,
        start + marker.length + selected.length,
      );
    });
  };

  const handleEditDichiarazione = (item) => {
    setDichNomeFile(item.nomeFile || "");
    setDichOggetto(item.oggetto || "");
    setDichCorpo(item.corpo || "");
    setDichLuogo(item.luogo || "");
    setDichData(item.data || todayInputValue());
    setDichEditingId(item.id);
    setDichFeedback({
      type: "success",
      text: "Dichiarazione caricata in modifica.",
    });
    setView("dichiarazioni");
  };

  const handleUsaComeBase = (item) => {
    setDichNomeFile(item.nomeFile || "");
    setDichOggetto(item.oggetto || "");
    setDichCorpo(item.corpo || "");
    setDichLuogo(item.luogo || "");
    setDichData(item.data || todayInputValue());
    setDichEditingId(null);
    setDichFeedback({
      type: "success",
      text: "Contenuto caricato come base per una nuova dichiarazione.",
    });
    setView("dichiarazioni");
  };

  const handleSaveDichiarazione = async () => {
    if (!dichOggetto.trim() || !dichCorpo.trim()) {
      setDichFeedback({
        type: "error",
        text: "Inserisci oggetto e corpo della dichiarazione.",
      });
      return;
    }

    const payload = {
      nomeFile: dichNomeFile.trim(),
      oggetto: dichOggetto.trim(),
      corpo: dichCorpo,
      luogo: dichLuogo.trim(),
      data: dichData,
      updatedAt: timestamp.fromDate(new Date()),
      updatedBy: user?.uid || null,
    };

    try {
      if (dichEditingId) {
        await updateDichiarazione(dichEditingId, payload);
        setDichFeedback({ type: "success", text: "Dichiarazione aggiornata." });
        setDichEditingId(null);
      } else {
        await addDichiarazione({ ...payload, createdBy: user?.uid || null });
        setDichFeedback({ type: "success", text: "Dichiarazione salvata." });
      }
    } catch (err) {
      console.error("Errore salvataggio dichiarazione:", err);
      setDichFeedback({
        type: "error",
        text: "Salvataggio della dichiarazione non riuscito.",
      });
    }
  };

  const handleDeleteDichiarazione = async (item) => {
    try {
      await deleteDichiarazione(item.id);
      if (dichEditingId === item.id) setDichEditingId(null);
      setDichFeedback({ type: "success", text: "Dichiarazione eliminata." });
    } catch (err) {
      console.error("Errore eliminazione dichiarazione:", err);
      setDichFeedback({
        type: "error",
        text: "Eliminazione della dichiarazione non riuscita.",
      });
    }
  };

  const handleGenerateDichiarazione = async () => {
    if (!dichOggetto.trim() || !dichCorpo.trim()) {
      setDichFeedback({
        type: "error",
        text: "Inserisci oggetto e corpo della dichiarazione.",
      });
      return;
    }

    const legale = documentiPerTipo.get(TIPO_LEGALE_RAPPRESENTANTE);
    setDichGenerating(true);
    setDichFeedback(null);
    try {
      await downloadDichiarazionePdf({
        oggetto: dichOggetto.trim(),
        corpo: dichCorpo,
        luogo: dichLuogo.trim(),
        data: dichData,
        fileName: dichNomeFile.trim() || undefined,
        allegato: legale?.fileUrl
          ? {
              url: legale.fileUrl,
              mimeType: legale.mimeType,
              fileName: legale.fileName,
            }
          : null,
      });
      setDichFeedback(
        legale?.fileUrl
          ? { type: "success", text: "Dichiarazione generata." }
          : {
              type: "error",
              text: 'PDF generato senza allegato: carica il "Documento Legale Rappresentante" per concatenarlo.',
            },
      );
    } catch (err) {
      console.error("Errore generazione dichiarazione:", err);
      setDichFeedback({
        type: "error",
        text: "Errore nella generazione del PDF.",
      });
    } finally {
      setDichGenerating(false);
    }
  };

  if (!admin) {
    return (
      <div className="documenti-aziendali">
        <div className="da-denied">
          <ShieldAlert size={40} />
          <h2>Accesso riservato agli amministratori</h2>
          <p>
            Questa sezione è disponibile solo per gli utenti con ruolo admin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="documenti-aziendali">
      <div className="da-header">
        <div>
          <h1>Documenti Aziendali</h1>
          <p>
            Archivio centralizzato dei documenti societari: consulta, scarica e
            aggiorna i documenti, con il controllo delle prossime scadenze.
          </p>
        </div>
      </div>

      <div className="da-tabs">
        <button
          type="button"
          className={`da-tab ${view === "documenti" ? "active" : ""}`}
          onClick={() => setView("documenti")}
        >
          Documenti
        </button>
        <button
          type="button"
          className={`da-tab ${view === "dichiarazioni" ? "active" : ""}`}
          onClick={() => setView("dichiarazioni")}
        >
          Dichiarazioni
        </button>
      </div>

      {view === "documenti" && (
        <>
          <div className="da-summary">
            <div className="da-summary-card">
              <span className="da-summary-label">Totale</span>
              <span className="da-summary-value">{statistiche.totale}</span>
            </div>
            <div className="da-summary-card disponibili">
              <span className="da-summary-label">Disponibili</span>
              <span className="da-summary-value">
                {statistiche.disponibili}
              </span>
            </div>
            <div className="da-summary-card mancanti">
              <span className="da-summary-label">Da caricare</span>
              <span className="da-summary-value">{statistiche.mancanti}</span>
            </div>
            <div className="da-summary-card in-scadenza">
              <span className="da-summary-label">In scadenza</span>
              <span className="da-summary-value">{statistiche.inScadenza}</span>
            </div>
            <div className="da-summary-card scaduti">
              <span className="da-summary-label">Scaduti</span>
              <span className="da-summary-value">{statistiche.scaduti}</span>
            </div>
          </div>

          <div className="da-panel">
            <div className="da-panel-header">
              <h3>
                <AlarmClock size={18} /> Prossime scadenze
              </h3>
            </div>
            {prossimeScadenze.length === 0 ? (
              <p className="da-empty">
                Nessun documento in scadenza nei prossimi 90 giorni. Aggiorna un
                documento indicando la data di scadenza per monitorarla qui.
              </p>
            ) : (
              <ul className="da-deadlines">
                {prossimeScadenze.map((row) => {
                  const stato = getStatoDocumento(row);
                  const giorniLabel =
                    row.giorni < 0
                      ? `Scaduto da ${Math.abs(row.giorni)} ${
                          Math.abs(row.giorni) === 1 ? "giorno" : "giorni"
                        }`
                      : row.giorni === 0
                        ? "Scade oggi"
                        : `Tra ${row.giorni} ${
                            row.giorni === 1 ? "giorno" : "giorni"
                          }`;
                  return (
                    <li key={row.tipo} className="da-deadline-row">
                      <div className="da-deadline-doc">
                        <FileText size={16} />
                        <span>{row.nome}</span>
                      </div>
                      <span className="da-deadline-date">
                        {formatData(row.dataScadenza)}
                      </span>
                      <span className={`da-badge ${stato.chiave}`}>
                        {giorniLabel}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="da-toolbar">
            <div className="da-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Cerca documento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {feedback && (
            <div className={`da-feedback ${feedback.type}`}>
              {feedback.text}
            </div>
          )}

          {error && (
            <div className="da-feedback error">
              Impossibile recuperare i documenti dal database.
            </div>
          )}

          <div className="da-grid">
            {righeFiltrate.map((row) => {
              const stato = getStatoDocumento(row);
              const isEditing = editingTipo === row.tipo;
              const isSaving = savingTipo === row.tipo;
              return (
                <div key={row.tipo} className="da-card">
                  <div className="da-card-head">
                    <div className="da-card-title">
                      <FileText size={18} />
                      <div>
                        <h4>{row.nome}</h4>
                        <span>{row.descrizione}</span>
                      </div>
                    </div>
                    <span className={`da-badge ${stato.chiave}`}>
                      {stato.label}
                    </span>
                  </div>

                  <div className="da-card-body">
                    <div className="da-meta">
                      <span>Emissione</span>
                      <strong>{formatData(row.dataEmissione)}</strong>
                    </div>
                    <div className="da-meta">
                      <span>Scadenza</span>
                      <strong>{formatData(row.dataScadenza)}</strong>
                    </div>
                    <div className="da-meta">
                      <span>Ultimo aggiornamento</span>
                      <strong>
                        {row.updatedAt?.toDate
                          ? format(row.updatedAt.toDate(), "dd/MM/yyyy HH:mm", {
                              locale: it,
                            })
                          : "—"}
                      </strong>
                    </div>
                    {row.note && <p className="da-note">{row.note}</p>}
                    {row.fileName && (
                      <p className="da-file">
                        <FileText size={14} /> {row.fileName}
                      </p>
                    )}
                  </div>

                  {!isEditing && (
                    <div className="da-card-actions">
                      {row.fileUrl ? (
                        <a
                          className="da-btn primary"
                          href={row.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download size={15} /> Scarica
                        </a>
                      ) : (
                        <span className="da-btn disabled">
                          <CircleDashed size={15} /> Nessun file
                        </span>
                      )}
                      <button
                        type="button"
                        className="da-btn"
                        onClick={() => startEdit(row)}
                      >
                        {row.fileUrl ? (
                          <>
                            <Pencil size={15} /> Aggiorna
                          </>
                        ) : (
                          <>
                            <Upload size={15} /> Carica
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {isEditing && (
                    <div className="da-form">
                      <label>
                        <span>File (PDF o immagine)</span>
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={(e) =>
                            setFormFile(e.target.files[0] || null)
                          }
                        />
                      </label>
                      <div className="da-form-row">
                        <label>
                          <span>Data emissione</span>
                          <input
                            type="date"
                            value={formDataEmissione}
                            onChange={(e) =>
                              setFormDataEmissione(e.target.value)
                            }
                          />
                        </label>
                        <label>
                          <span>Data scadenza</span>
                          <input
                            type="date"
                            value={formDataScadenza}
                            onChange={(e) =>
                              setFormDataScadenza(e.target.value)
                            }
                          />
                        </label>
                      </div>
                      <label>
                        <span>Note</span>
                        <textarea
                          rows={2}
                          value={formNote}
                          onChange={(e) => setFormNote(e.target.value)}
                        />
                      </label>

                      {isSaving && formFile && (
                        <div className="da-progress">
                          <div
                            className="da-progress-bar"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      )}

                      <div className="da-form-actions">
                        <button
                          type="button"
                          className="da-btn primary"
                          onClick={() => handleSave(row)}
                          disabled={isSaving}
                        >
                          <CheckCircle2 size={15} />
                          {isSaving ? "Salvataggio..." : "Salva"}
                        </button>
                        <button
                          type="button"
                          className="da-btn"
                          onClick={resetForm}
                          disabled={isSaving}
                        >
                          <X size={15} /> Annulla
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {documents && righeFiltrate.length === 0 && (
            <p className="da-empty">
              Nessun documento corrisponde alla ricerca.
            </p>
          )}
        </>
      )}

      {view === "dichiarazioni" && (
        <>
          <div className="da-panel">
            <div className="da-panel-header">
              <h3>
                <FileText size={18} /> Dichiarazione su carta intestata
              </h3>
            </div>

            <div className="da-dich-form">
              <label>
                <span>Nome file</span>
                <input
                  type="text"
                  className="da-dich-oggetto"
                  value={dichNomeFile}
                  onChange={(e) => setDichNomeFile(e.target.value)}
                  placeholder="es. Dichiarazione_CCIAA_2026"
                />
              </label>

              <label>
                <span>Oggetto</span>
                <input
                  type="text"
                  className="da-dich-oggetto"
                  value={dichOggetto}
                  onChange={(e) => setDichOggetto(e.target.value)}
                  placeholder="Titolo della dichiarazione"
                />
              </label>

              <label>
                <span>Corpo della dichiarazione</span>
                <textarea
                  ref={dichCorpoRef}
                  rows={16}
                  className="da-dich-corpo"
                  value={dichCorpo}
                  onChange={(e) => setDichCorpo(e.target.value)}
                  placeholder="Testo della dichiarazione (dopo 'DICHIARA')"
                />
              </label>

              <div className="da-dich-toolbar">
                <button
                  type="button"
                  className="da-btn"
                  onClick={() => applyMarkup("**")}
                  title="Grassetto"
                >
                  <strong>G</strong> Grassetto
                </button>
                <button
                  type="button"
                  className="da-btn"
                  onClick={() => applyMarkup("*")}
                  title="Corsivo"
                >
                  <em>C</em> Corsivo
                </button>
                <span className="da-dich-toolbar-hint">
                  Il testo conserva i ritorni a capo; una riga vuota crea un
                  nuovo paragrafo. Grassetto e corsivo tramite i pulsanti.
                </span>
              </div>

              <div className="da-form-row">
                <label>
                  <span>Luogo</span>
                  <input
                    type="text"
                    value={dichLuogo}
                    onChange={(e) => setDichLuogo(e.target.value)}
                    placeholder="es. Milano"
                  />
                </label>
                <label>
                  <span>Data</span>
                  <input
                    type="date"
                    value={dichData}
                    onChange={(e) => setDichData(e.target.value)}
                  />
                </label>
              </div>

              <p className="da-hint">
                Al PDF vengono aggiunti automaticamente il testo base del
                dichiarante e, in coda, il{" "}
                {documentiPerTipo.get(TIPO_LEGALE_RAPPRESENTANTE)?.fileUrl ? (
                  <strong>Documento Legale Rappresentante caricato</strong>
                ) : (
                  <strong>
                    Documento Legale Rappresentante (non caricato)
                  </strong>
                )}
                .
              </p>

              {dichFeedback && (
                <div className={`da-feedback ${dichFeedback.type}`}>
                  {dichFeedback.text}
                </div>
              )}

              <div className="da-form-actions">
                <button
                  type="button"
                  className="da-btn primary"
                  onClick={handleGenerateDichiarazione}
                  disabled={dichGenerating}
                >
                  <Download size={15} />
                  {dichGenerating ? "Generazione..." : "Genera PDF"}
                </button>
                <button
                  type="button"
                  className="da-btn"
                  onClick={handleSaveDichiarazione}
                >
                  <CheckCircle2 size={15} />
                  {dichEditingId ? "Salva modifiche" : "Salva dichiarazione"}
                </button>
                <button
                  type="button"
                  className="da-btn"
                  onClick={resetDichForm}
                >
                  <X size={15} /> Nuova
                </button>
              </div>
            </div>
          </div>

          <div className="da-panel">
            <div className="da-panel-header">
              <h3>
                <FolderOpen size={18} /> Dichiarazioni salvate
              </h3>
            </div>
            {!dichiarazioni || dichiarazioni.length === 0 ? (
              <p className="da-empty">
                Nessuna dichiarazione salvata. Compila il modulo e salva per
                riutilizzarla come base.
              </p>
            ) : (
              <ul className="da-dich-list">
                {dichiarazioni.map((item) => (
                  <li
                    key={item.id}
                    className={`da-dich-item ${
                      dichEditingId === item.id ? "selected" : ""
                    }`}
                  >
                    <div className="da-dich-info">
                      <strong>{item.nomeFile || item.oggetto}</strong>
                      <span>
                        {item.nomeFile ? `${item.oggetto} · ` : ""}
                        {[item.luogo, formatData(item.data)]
                          .filter(Boolean)
                          .join(", ")}
                        {item.updatedAt?.toDate
                          ? ` · ${format(
                              item.updatedAt.toDate(),
                              "dd/MM/yyyy HH:mm",
                              { locale: it },
                            )}`
                          : ""}
                      </span>
                    </div>
                    <div className="da-card-actions">
                      <button
                        type="button"
                        className="da-btn"
                        onClick={() => handleEditDichiarazione(item)}
                      >
                        <Pencil size={15} /> Modifica
                      </button>
                      <button
                        type="button"
                        className="da-btn"
                        onClick={() => handleUsaComeBase(item)}
                      >
                        <Plus size={15} /> Usa come base
                      </button>
                      <button
                        type="button"
                        className="da-btn danger"
                        onClick={() => handleDeleteDichiarazione(item)}
                      >
                        <Trash2 size={15} /> Elimina
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
